import React, { PureComponent } from "react";
import { Modal, Button } from "react-bootstrap";
import { Scrollbars } from "react-custom-scrollbars";
import Radium, { StyleRoot } from "radium";
import { fadeIn } from "react-animations";
import { pure } from "recompose";
import { map, flatten, partition, isNil } from "lodash";

import Auth from "routes/Auth";
import EventEmitter from "services/event-emitter";
import { hexToRgba, detectMobile, setGetStartedShown } from "services/utils";
import Teams from "api/Teams";

const fadeInStyle = {
  animation: "x 1s",
  animationName: Radium.keyframes(fadeIn, "fadeIn")
};

const renderTeamPlayer = ({ id, name, role, user }) => {
  const roleStrings = {
    CEO: "CEO",
    PD: "Head of Product Management",
    PM: "Head of Product Marketing",
    OP: "Head of Operations",
    FI: "Head of Finance",
    VI: "Viewer"
  };

  return (
    <div key={id} className="playerBlock">
      <div className="playerBlock__circle" />
      <div>
        {user ? (
          <p className="playerBlock__name">
            {user.first_name} {user.last_name}
          </p>
        ) : (
          <p className="playerBlock__name">Unassigned</p>
        )}
        <p className="playerBlock__role">{roleStrings[role]}</p>
      </div>
    </div>
  );
};

const renderTeamMembers = () => (
  <div className="step5__right">
    {flatten(
      map(
        partition(
          Teams.players().filter(player => player.role !== "VI"),
          player => !isNil(player.user)
        ),
        list => map(list, renderTeamPlayer)
      )
    )}
  </div>
);

const Step1 = pure(() => (
  <StyleRoot>
    <p className="steps__text" style={fadeInStyle}>
      Market Games is a team based business competition
    </p>
  </StyleRoot>
));

const Step2 = pure(() => (
  <StyleRoot>
    <div className="step2" style={fadeInStyle}>
      <p className="steps__text">1. The Goal</p>
      <p className="steps__text">2. Your Role</p>
      <p className="steps__text">3. Your Team</p>
    </div>
  </StyleRoot>
));

const Step3 = pure(() => (
  <div className="step3">
    <p className="steps__text" style={fadeInStyle}>
      Your goal is to capture more market share and profits than your
      competitors and your grades are determined by how your team performs on
      key metrics relative to your competitors.
    </p>
    <p className="steps__text">
      For more information on grading, please review our grading guidelines.
    </p>
  </div>
));

const Step4 = pure(({ getRoleDescription }) => (
  <StyleRoot>
    <div className="step4" style={fadeInStyle}>
      {getRoleDescription()}
    </div>
  </StyleRoot>
));

export const Step5 = pure(() => (
  <div className="step5" style={fadeInStyle}>
    <div className="step5__left">
      <p className="steps__text">
        You will be working closely with your team to make decisions throughout
        the course of this game.
      </p>
      <p className="steps__text">
        Be sure to check in with your team when making decisions.
      </p>
    </div>
    {renderTeamMembers()}
  </div>
));

class GetStartedModal extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      show: true,
      step: 1
    };

    this.eventemitter = EventEmitter.getInstance();
    this.isMobile = detectMobile();
  }

  componentDidMount() {
    this.eventemitter.on("open-get-started-modal", this.show);
    this.eventemitter.on("open-guide-after-role", this.showAfterRole);
  }

  componentWillUnmount() {
    this.eventemitter.off("open-get-started-modal", this.show);
    this.eventemitter.off("open-guide-after-role", this.showAfterRole);
  }

  show = () => {
    this.setState({
      show: true,
      step: 1
    });
  };

  hide = () => {
    this.props.onFinish();
  };

  showAfterRole = () => {
    this.setState({
      show: true,
      step: 4
    });
  };

  showRole = e => {
    e.preventDefault();
    this.setState(
      {
        show: false
      },
      () => {
        this.eventemitter.trigger("open-from-guide");
      }
    );
  };

  getRoleRaw = () => Auth.getRole(this.props.activeRound);

  get currentRole() {
    const { activeRound } = this.props;
    const role = Auth.getRole(activeRound).toLowerCase();

    if (role === "ceo") {
      return "CEO";
    } else if (role === "pd") {
      return "Product Development";
    } else if (role === "pm") {
      return "Product Marketing";
    } else if (role === "op") {
      return "Operations";
    } else if (role === "fi") {
      return "Finance";
    }

    return "";
  }

  prev = () => {
    const { step } = this.state;

    if (step > 1) {
      this.setState({
        step: step - 1
      });
    }
  };

  next = () => {
    const { step } = this.state;

    if (step < 5) {
      this.setState({
        step: step + 1
      });
    } else {
      setGetStartedShown()
        .then(() => {
          this.props.onFinish();
        });
    }
  };

  getFullName() {
    const { first_name, last_name } = Auth.getUser();
    return `${first_name} ${last_name}`;
  }

  renderThumbVertical = ({ style, ...props }) => {
    const { team_color = "#fff" } = this.props;

    return (
      <div
        {...props}
        style={{
          ...style,
          backgroundColor: hexToRgba(team_color, 0.6),
          borderRadius: "5px"
        }}
        className="thumb-vertical"
      />
    );
  };

  getHeaderText = () => {
    const { step } = this.state;

    switch (step) {
      case 1:
        return `Welcome to Market Games, ${this.getFullName()}!`;
      case 2:
        return "This tutorial is going to cover everything you need to know to get you started";
      case 3:
        return "The Goal";
      case 4:
        return `Your Role: ${this.currentRole}`;
      case 5:
        return `Your Team: ${Teams.currentTeam().name}`;
      case 6:
        return "How to navigate and play the game";
      case 7:
        return "How to make decisions";
      default:
        return "Resources to help make decisions";
    }
  };

  getRoleDescription = () => {
    return (
      {
        CEO: (
          <div>
            <p className="steps__text">
              Your role is to work with your management team to develop and
              execute a strategy that will help your business achieves its
              goals. Your key responsibilities are:
            </p>
            <p className="steps__text">
              <b>Make Financial Projections</b> — You have to translate your
              strategy into financial goals in order to raise capital.
              Therefore, you have to make a 3 year financial projection.
            </p>
            <p className="steps__text">
              <b>Lead Team</b> — Ensure team decisions are sound and submitted.
            </p>
            <p className="steps__text">Note: Your role changes each year.</p>
          </div>
        ),
        PD: (
          <div>
            <p className="steps__text">
              You are the core of the business. You build products that drive
              revenues and ultimately profits. Your key responsibility is to
              develop product strategies and product designs that customers will
              demand now and into the future.
            </p>
            <p className="steps__text">Note: Your role changes each year.</p>
          </div>
        ),
        PM: (
          <div>
            <p className="steps__text">
              You know the customer better than any other department in your
              organization. Therefore you know how to reach them and their
              willingness to pay. Your key responsibilities are:
            </p>
            <p className="steps__text">
              <b>Price & Forecast</b> — You set the price for your business's
              products.
            </p>
            <p className="steps__text">
              <b>Promotion</b> — You decide on the channel(s) to market your
              business's products and the budget to spend.
            </p>
            <p className="steps__text">
              <b>Sales</b> — You decide on the channel to sell your products.
            </p>
            <p className="steps__text">Note: Your role changes each year.</p>
          </div>
        ),
        OP: (
          <div>
            <p className="steps__text">
              You are critical to your business because you drive efficiency.
              Your role is to build quality products at the lowest costs.
            </p>
            <p className="steps__text">
              <b>Decide the Facility</b> — you have to decide if you want to
              produce in-house or outsource production.
            </p>
            <p className="steps__text">
              <b>Decide on the machines</b> — you have to decide on the
              machine(s) that you will use to produce the products.
            </p>
            <p className="steps__text">
              <b>Decide on the # of units to produce</b> — you have to decide on
              how many units to produce.
            </p>
            <p className="steps__text">Note: Your role changes each year.</p>
          </div>
        ),
        FI: (
          <div>
            <p className="steps__text">
              You provide the fuel that drives growth. Your key responsibilities
              are:
            </p>
            <p className="steps__text">
              <b>Raising Capital</b> — You have to decide how much capital does
              your business need and the form of capital (i.e. debt and/or
              equity).
            </p>
            <p className="steps__text">
              <b>Managing Capital</b> — You have to periodically assess how
              effective the business has been in using capital by performing
              various financial analysis.
            </p>
            <p className="steps__text">
              <b>Returning Capital</b> — You have to decide when and how much
              capital should be returned to investors.
            </p>
            <p className="steps__text">Note: Your role changes each year.</p>
          </div>
        ),
        VI: <div>Viewer</div>
      }[this.getRoleRaw()] || <div />
    );
  };

  render() {
    const { show, step } = this.state;
    const { team_color, className } = this.props;
    const captionStyle = { color: team_color };
    const scrollBarsSettings = {
      style: {
        height: "calc(100% - 60px)"
      },
      renderThumbVertical: this.renderThumbVertical
    };
    const nextOnLastStep = this.props.nextOnLastStep || false;

    return (
      <Modal
        show={show}
        onHide={this.hide}
        dialogClassName={`${className} feedback-modal`}
        backdrop="static"
        backdropClassName="feedback-backdrop"
      >
        <Modal.Body>
          <Scrollbars {...scrollBarsSettings}>
            <div className="steps" style={captionStyle}>
              <div className="steps__icon" />

              <div>
                <p
                  className={
                    step === 1
                      ? "steps__title steps__title_one"
                      : "steps__title"
                  }
                >
                  {this.getHeaderText()}
                </p>
                {{
                  1: <Step1 />,
                  2: <Step2 />,
                  3: <Step3 />,
                  4: <Step4 getRoleDescription={this.getRoleDescription} />,
                  5: <Step5 />
                }[step] || null}
              </div>
            </div>
          </Scrollbars>
          <div className="feedback-button">
            {step > 1 && (
              <Button
                className="backBtn"
                bsSize={this.isMobile === true ? "md" : "lg"}
                onClick={this.prev}
              >
                Back
              </Button>
            )}
            <Button
              bsStyle="primary"
              bsSize={this.isMobile === true ? "md" : "lg"}
              onClick={this.next}
            >
              {step === 5 && !nextOnLastStep ? "Close" : "Continue"}
              <span />
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }
}

export default GetStartedModal;



// WEBPACK FOOTER //
// ./src/components/get-started/modal.js